<template>
  <div v-show="isCustomFieldValid" @click="handleClick">
    <div v-if="!props.hideLabel" class="tw-flex" @click.stop>
      <label
        class="input-label tw-flex tw-min-w-0 tw-items-center"
        :for="customField.name"
      >
        <div class="tw-min-w-0">
          <tooltip-text
            :text="customField.name"
            max-width="100%"
          ></tooltip-text>
        </div>
        <help-text v-if="!!customField.description" class="tw-mb-0.5 tw-ml-1">
          {{ customField.description }}
        </help-text>
      </label>
      <div class="tw-ml-auto tw-shrink-0">
        <div
          class="tw-origin-right tw-scale-75 tw-rounded tw-bg-primary tw-px-1 tw-text-xs tw-font-bold tw-text-white"
        >
          CALCULATED
        </div>
      </div>
    </div>

    <div class="tw-relative" title="Click to show breakdown">
      <button
        class="tw-flex tw-h-8 tw-w-full tw-items-center tw-rounded tw-border tw-border-dashed tw-border-gray-medium tw-px-2 tw-py-1 tw-text-xs"
        @click="showDialog"
      >
        <tooltip-text :text="formattedValue" max-width="100%"></tooltip-text>
      </button>
    </div>
    <calculated-custom-field-dialog
      v-if="props.item && isCustomFieldValid"
      ref="calculatedDialogRef"
      :item="props.item"
      :custom-field="customField"
    />
  </div>
</template>

<script setup lang="ts">
import { PreparedWorkItem } from '@/models/item.models';

import { ref, computed } from 'vue';
import HelpText from '@/components/design-system/misc/HelpText.vue';
import CalculatedCustomFieldDialog from './CalculatedCustomFieldDialog.vue';
import TooltipText from '../core/TooltipText.vue';
import dayjs from '@/utils/dayjs';
import { useIntl } from '@/utils/internationalization';
import {
  CustomField,
  CustomFieldCalculatedSubType,
  CustomFieldType,
} from '@/api';

interface Props {
  customField: CustomField & { required?: boolean };
  value?: string | number | boolean;
  hideLabel?: boolean;
  item?: PreparedWorkItem;
}
interface Emits {
  (e: 'value-updated', value: string | null): void;
}

const props = withDefaults(defineProps<Props>(), {});
defineEmits<Emits>();

const calculatedDialogRef =
  ref<InstanceType<typeof CalculatedCustomFieldDialog>>();
const { dateTimeFormat } = useIntl();

const isCustomFieldValid = computed(() => {
  return props.customField.type === CustomFieldType.CALCULATED;
});

const formattedValue = computed<string>(() => {
  const val = props.value;
  if (!isCustomFieldValid.value || typeof val === 'undefined') return '';

  if (props.customField.subType === CustomFieldCalculatedSubType.BOOLEAN) {
    return val ? 'True' : 'False';
  }

  if (props.customField.subType === CustomFieldCalculatedSubType.DATE) {
    const isValidDate =
      val && typeof val === 'string' && val && dayjs(val).isValid();
    if (!isValidDate) return val.toString();
    return dayjs(val).format(dateTimeFormat.value.date);
  }

  if (props.customField.subType === CustomFieldCalculatedSubType.NUMBER) {
    return val ? val.toString() : '';
  }

  return val.toString(); // string
});

const handleClick = () => {
  // Do nothing - click handled on button
};

function showDialog() {
  if (props.item) {
    calculatedDialogRef.value?.showDialog();
  }
}

defineExpose({
  validate: () => {
    // just for consistency with other custom fields
  },
  removeError: () => {
    // just for consistency with other custom fields
  },
  isValid: computed(() => true),
});
</script>

<style lang="scss" scoped>
button {
  &:hover {
    @apply tw-border-gray-dark;
  }

  &:focus {
    border-color: var(--q-primary, var(--primary-color)) !important;
    outline: none;
  }
}
</style>
